import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, TextArea } from '@/lib';
import { CampaignStepTitle } from '../../common';
import { BaseCampaignStep } from '../BaseCampaignStep';
import { CreateCampaignFormInput } from '../../../validation';
import { useCampaignsActions } from '../../../context';

export const NewCampaignStep: FC = () => {
  const { t } = useTranslation('campaign');
  const {
    register,
    formState: { errors, dirtyFields, touchedFields },
    trigger,
    watch,
  } = useFormContext<CreateCampaignFormInput>();

  const { setNextStep } = useCampaignsActions();

  const campaignName = watch('name');
  const campaignDescription = watch('description');

  const canGoNextStep = dirtyFields.name && !errors.name && !errors.description;

  // Validate name when input change or field is touched (since it's required)
  useEffect(() => {
    if (dirtyFields.name || touchedFields.name) {
      trigger('name');
    }
  }, [campaignName, trigger, dirtyFields.name, touchedFields.name]);

  // Validate description on input change (no need touched since it's not required)
  useEffect(() => {
    if (dirtyFields.description) {
      trigger('description');
    }
  }, [campaignDescription, trigger, dirtyFields.description]);

  const handleStartCreationClick = async () => {
    await trigger(['name', 'description']);

    if (canGoNextStep) {
      setNextStep();
    }
  };

  return (
    <BaseCampaignStep className='pt-0 lg:bg-[url("/assets/image/create-campaign-step-bg.svg")] lg:bg-no-repeat lg:bg-contain lg:bg-[position:calc(100%+40px)_100%] 2xl:bg-[position:calc(100%+40px)_100%]'>
      <CampaignStepTitle classNames={{ wrapper: 'pt-28' }} title='createNewCampaign_title' />

      <div className='max-w-[380px] flex flex-col gap-6'>
        <div className='flex flex-col gap-5'>
          <div>
            <Label requiredSignClassName='text-brand-600' htmlFor='name' isRequired>
              {t('enterCampaignTitleLabel')}
            </Label>
            <Input {...register('name')} placeholder={t('enterCampaignTitlePlaceholder')} errorMsg={errors.name?.message} />
          </div>

          <div>
            <Label htmlFor='description'>{t('enterCampaignDescriptionLabel')}</Label>
            <TextArea
              className='max-h-36'
              {...register('description')}
              placeholder={t('enterCampaignDescriptionPlaceholder')}
              errorMsg={errors.description?.message}
            />
          </div>
        </div>

        <Button className='w-full' text={t('startCreateFlow')} onClick={handleStartCreationClick} disabled={!canGoNextStep} />
      </div>
    </BaseCampaignStep>
  );
};
